import * as React from "react";
import { cn } from "../../lib/utils.js";

/**
 * Table primitive — always wraps the native <table> in a scroll container so
 * narrow viewports can pan without layout-breaking.
 *
 * Dos modos, porque el producto tiene dos tablas de verdad distintas:
 *
 *   - **acotada** (default): la tabla vive dentro de una card y scrollea con la
 *     página. Cabecera rellena en surface-2.
 *   - **fill**: la tabla OCUPA su contenedor y scrollea por dentro, con la
 *     cabecera pegada arriba (`<Table.Head sticky>`). Es la tabla de datos de
 *     admin — la que llena el viewport y lleva checkbox, orden y menú de fila.
 *     Existía en 6 pantallas escrita a mano porque el primitivo no la tenía.
 *
 * Props:
 *   - mobileStack: bajo `md` la tabla colapsa a cards apiladas — thead fuera,
 *     cada <tr> es una card y cada <td> una línea label/valor. El label sale
 *     de `data-label` en <Table.Cell>; una celda sin data-label se alinea a la
 *     derecha sin rótulo (checkboxes, menú ⋮), y `cardTitle` marca cuál celda
 *     titula la tarjeta. El colapso vive en `index.css` (§ Table mobileStack)
 *     sobre la clase `mobile-stack`.
 *   - fill: modo full-height. Solo aporta el comportamiento de scroll; el
 *     borde, el fondo y el radio los pone el contenedor del consumidor, que en
 *     estas pantallas también envuelve la barra de filtros y la paginación.
 *     El padre debe ser flex column con altura acotada.
 *
 * Los dos props se combinan: `fill mobileStack` es la tabla de datos de admin
 * —Flota— que en escritorio llena el viewport con la cabecera pegada y bajo
 * `md` colapsa a tarjetas. Es lo que dejó a esas pantallas mantener un solo
 * árbol: antes llevaban la tabla y, en un bloque `md:hidden` aparte, una lista
 * de tarjetas con los mismos datos escritos por segunda vez (el componente que
 * las armaba, `EntityCard`, se quedó sin consumidores y ya no existe). En ese modo
 * el scroll interno es solo de `md` para arriba: bajo `md` las tarjetas
 * scrollean con la página, como en el resto del panel.
 *
 * Sub-components: Head, Body, Row, HeaderCell, Cell
 */
export function Table({
  className,
  mobileStack = false,
  fill = false,
  children,
  ...props
}) {
  return (
    <div
      className={cn(
        fill
          ? mobileStack
            ? "w-full md:flex-1 md:min-h-0 md:overflow-auto"
            : "flex-1 min-h-0 overflow-auto"
          : mobileStack
            ? // Bajo md las filas son cards sueltas: el borde/radio del wrapper
              // alrededor de cards se vería como una caja fantasma.
              "w-full md:overflow-x-auto md:rounded-md md:border md:border-border-muted md:bg-surface-1 md:sheen-1 md:shadow-card"
            : "w-full overflow-x-auto rounded-md border border-border-muted bg-surface-1 sheen-1 shadow-card",
        className,
      )}
      {...props}
    >
      <table
        className={cn(
          "w-full text-sm text-text-primary",
          mobileStack && "mobile-stack",
        )}
      >
        {children}
      </table>
    </div>
  );
}

/**
 * @param {boolean} [sticky] — cabecera pegada al tope del contenedor de scroll.
 *   El fondo va en el <tr> (`[&_tr]:`), no en el <thead>: con `border-collapse`
 *   el fondo del thead no pinta de forma fiable y las filas se transparentan
 *   por debajo al scrollear.
 */
function TableHead({ className, sticky = false, children, ...props }) {
  return (
    <thead
      className={cn(
        "text-xs uppercase tracking-wide",
        sticky
          ? "sticky top-0 z-10 text-text-dim [&_tr]:bg-surface-2 [&_tr]:sheen-chrome [&_th]:border-b [&_th]:border-border"
          : "bg-surface-2 sheen-chrome text-text-secondary",
        className,
      )}
      {...props}
    >
      {children}
    </thead>
  );
}
TableHead.displayName = "Table.Head";

function TableBody({ className, children, ...props }) {
  return (
    <tbody
      className={cn("divide-y divide-border-muted", className)}
      {...props}
    >
      {children}
    </tbody>
  );
}
TableBody.displayName = "Table.Body";

/**
 * La fila par lleva un tinte, para que el ojo no tenga que seguir el renglón
 * con el dedo en una tabla de diez columnas. Va con el token de ESTADO
 * (`surface-2`) al 40%, no con un gris propio: es el mismo tinte del hover y
 * del activo, así que se recalcula solo en los dos temas —sobre blanco baja a
 * un gris azulado apenas perceptible, sobre el oscuro sube un escalón— y nunca
 * compite con los colores semánticos de las celdas.
 *
 * Solo desde `md`: bajo ese ancho la fila es una TARJETA suelta, y rayar una
 * sí y otra no se leería como dos tipos de tarjeta.
 *
 * El hover sigue ganando: `even` y `hover` pesan lo mismo en especificidad, y
 * Tailwind emite `hover` después. Si alguna vez se invirtiera, el síntoma
 * sería un hover que no se ve en las filas pares.
 */
function TableRow({ className, children, ...props }) {
  return (
    <tr
      className={cn(
        "md:even:bg-surface-2/40 hover:bg-surface-2/60 transition-colors",
        className,
      )}
      {...props}
    >
      {children}
    </tr>
  );
}
TableRow.displayName = "Table.Row";

/**
 * @param {boolean} [dense] — geometría de la tabla de datos de admin: menos aire
 *   lateral y más vertical, porque la fila carga checkbox y menú de acciones.
 *   Es la definición ÚNICA de esa celda: antes vivía copiada a mano en 19
 *   sitios, incluido el interior de SortableHeader.
 */
function TableHeaderCell({ className, dense = false, children, ...props }) {
  return (
    <th
      scope="col"
      className={cn(
        "text-left",
        dense ? "px-3 py-3 font-medium" : "px-4 py-2 font-semibold",
        className,
      )}
      {...props}
    >
      {children}
    </th>
  );
}
TableHeaderCell.displayName = "Table.HeaderCell";

/**
 * @param {boolean} [cardTitle] — en `mobileStack`, esta celda titula la tarjeta:
 *   banda propia a lo ancho, tipografía mayor y arriba del todo. Es para el
 *   IDENTIFICADOR del dato (el cliente, la placa), no para la primera columna
 *   que le tocó a la tabla: en Facturas la primera es el número y en Pagos la
 *   fecha, y una lista de tarjetas se escanea por el nombre de la entidad.
 *   Sin este prop titula la primera celda. Una por fila. El orden de columnas
 *   del escritorio no se toca — el ascenso es solo dentro de la tarjeta
 *   (§ Table mobileStack en `index.css`).
 * @param {boolean} [dense] — la geometría de la tabla de datos de admin, en
 *   espejo de `Table.HeaderCell dense`: menos aire lateral y más vertical,
 *   porque la fila carga checkbox y menú de acciones. En `mobileStack` no se
 *   nota: el ritmo de la tarjeta lo pone el CSS del colapso.
 * @param {boolean} [selectCell] — la celda del checkbox de selección. En
 *   `mobileStack` se ancla a la izquierda de la banda de cabecera, en espejo
 *   del ⋮; sin esto caería en la rejilla del cuerpo y un cuadradito de 14 px
 *   se quedaría con media fila de la tarjeta. Es lo que le faltaba a
 *   `mobileStack` para que Flota —selección múltiple + BulkBar— pudiera dejar
 *   de mantener un árbol de tarjetas aparte. Pide `cardTitle` explícito en la
 *   fila: con una primera celda sin rótulo, el fallback de "titula la primera
 *   celda" no tiene a qué agarrarse.
 * @param {boolean} [cardActions] — la celda de acciones cuando NO es un ⋮ sino
 *   botones con texto. El ancla por defecto de una celda sin rótulo es la
 *   esquina superior derecha de la tarjeta: da para un icono de 28 px, no para
 *   un par de botones. Con esto la celda baja al pie de la tarjeta, ocupa la
 *   rejilla a lo ancho y estira a su hijo. En escritorio no cambia nada — sigue
 *   siendo la celda normal de la última columna.
 */
function TableCell({
  className,
  cardTitle = false,
  selectCell = false,
  cardActions = false,
  dense = false,
  children,
  ...props
}) {
  return (
    <td
      className={cn(
        dense ? "px-3 py-3" : "px-4 py-2",
        "align-middle",
        className,
      )}
      data-card-title={cardTitle ? "" : undefined}
      data-card-select={selectCell ? "" : undefined}
      data-card-actions={cardActions ? "" : undefined}
      {...props}
    >
      {children}
    </td>
  );
}
TableCell.displayName = "Table.Cell";

Table.Head = TableHead;
Table.Body = TableBody;
Table.Row = TableRow;
Table.HeaderCell = TableHeaderCell;
Table.Cell = TableCell;
